import logger from '@utils/logger'
import { isPrivacyCategoryAllowed } from '@/privacy/privacyPreferences'

const MARKETING_CACHE_PREFIX = 'x3algo-client-'
const OPTIONAL_CACHE_NAMES = new Set([
  'blog-posts-cache',
  'blog-images-cache',
  'images-cache',
  'x3algo-client-navigation-v1',
  'x3algo-client-navigation-v2',
  'x3algo-client-blog-posts-v1',
  'x3algo-client-blog-images-v1',
  'x3algo-client-images-v1'
])
const WORKBOX_EXPIRATION_DATABASE = 'workbox-expiration'
const WORKBOX_EXPIRATION_STORE = 'cache-entries'

let serviceWorkerRegistrationPromise = null

function getMarketingBaseUrl() {
  return new URL(import.meta.env.BASE_URL || '/', window.location.origin)
}

function getWorkerScript(worker) {
  return worker?.scriptURL
    ? new URL(worker.scriptURL, window.location.origin)
    : null
}

function isMarketingWorker(worker) {
  const script = getWorkerScript(worker)
  const expectedScript = new URL('sw.js', getMarketingBaseUrl())
  return Boolean(
    script &&
      script.origin === expectedScript.origin &&
      script.pathname === expectedScript.pathname
  )
}

function isMarketingRegistration(registration) {
  const expectedScope = getMarketingBaseUrl().href
  if (registration.scope !== expectedScope) return false

  const workers = [
    registration.active,
    registration.waiting,
    registration.installing
  ].filter(Boolean)

  return workers.length === 0 || workers.some(isMarketingWorker)
}

function isMarketingCacheName(cacheName) {
  return (
    OPTIONAL_CACHE_NAMES.has(cacheName) ||
    cacheName.startsWith(MARKETING_CACHE_PREFIX)
  )
}

async function clearLegacyPrecache(cacheName) {
  const cache = await window.caches.open(cacheName)
  const requests = await cache.keys()
  const marketingRequests = requests.filter(request => {
    const url = new URL(request.url)
    return (
      url.origin === window.location.origin &&
      !/^\/docs(?:\/|$)/.test(url.pathname)
    )
  })

  await Promise.all(marketingRequests.map(request => cache.delete(request)))

  if ((await cache.keys()).length === 0) {
    await window.caches.delete(cacheName)
  }
}

async function clearWorkboxExpirationEntries(cacheNames) {
  if (!window.indexedDB || cacheNames.length === 0) return

  if (window.indexedDB.databases) {
    try {
      const databases = await window.indexedDB.databases()
      if (
        !databases.some(
          database => database.name === WORKBOX_EXPIRATION_DATABASE
        )
      ) {
        return
      }
    } catch (_error) {
      // Continue with a guarded open for browsers that restrict database listing.
    }
  }

  await new Promise(resolve => {
    const openRequest = window.indexedDB.open(WORKBOX_EXPIRATION_DATABASE)

    openRequest.onupgradeneeded = event => {
      event.target.transaction.abort()
    }
    openRequest.onerror = () => resolve()
    openRequest.onsuccess = () => {
      const database = openRequest.result
      if (!database.objectStoreNames.contains(WORKBOX_EXPIRATION_STORE)) {
        database.close()
        resolve()
        return
      }

      try {
        const transaction = database.transaction(
          WORKBOX_EXPIRATION_STORE,
          'readwrite'
        )
        const store = transaction.objectStore(WORKBOX_EXPIRATION_STORE)
        const index = store.index('cacheName')

        cacheNames.forEach(cacheName => {
          const cursorRequest = index.openCursor(
            window.IDBKeyRange.only(cacheName)
          )
          cursorRequest.onsuccess = event => {
            const cursor = event.target.result
            if (!cursor) return
            cursor.delete()
            cursor.continue()
          }
        })

        transaction.oncomplete = () => {
          database.close()
          resolve()
        }
        transaction.onerror = () => {
          database.close()
          resolve()
        }
        transaction.onabort = () => {
          database.close()
          resolve()
        }
      } catch (_error) {
        database.close()
        resolve()
      }
    }
  })
}

async function clearMarketingCaches() {
  if (!('caches' in window)) return

  try {
    const cacheNames = await window.caches.keys()
    const marketingCacheNames = cacheNames.filter(isMarketingCacheName)
    const legacyPrecacheNames = cacheNames.filter(cacheName =>
      cacheName.startsWith('workbox-precache')
    )

    await Promise.all([
      ...marketingCacheNames.map(cacheName => window.caches.delete(cacheName)),
      ...legacyPrecacheNames.map(clearLegacyPrecache)
    ])
    await clearWorkboxExpirationEntries([
      ...OPTIONAL_CACHE_NAMES,
      ...marketingCacheNames
    ])
  } catch (error) {
    logger.warn('Could not clear offline caches:', error.message)
  }
}

export async function enableOfflineExperience() {
  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return null
  if (serviceWorkerRegistrationPromise) return serviceWorkerRegistrationPromise

  const baseUrl = getMarketingBaseUrl()
  const scriptUrl = new URL('sw.js', baseUrl)
  let registrationPromise

  registrationPromise = navigator.serviceWorker
    .register(scriptUrl.href, { scope: baseUrl.pathname })
    .then(async registration => {
      if (!isPrivacyCategoryAllowed('experience')) {
        await registration.unregister()
        await clearMarketingCaches()
        return null
      }

      logger.info('Offline experience is ready')
      return registration
    })
    .catch(error => {
      if (serviceWorkerRegistrationPromise === registrationPromise) {
        serviceWorkerRegistrationPromise = null
      }
      logger.warn('Offline experience is unavailable:', error.message)
      return null
    })

  serviceWorkerRegistrationPromise = registrationPromise
  return registrationPromise
}

export async function disableOfflineExperience() {
  serviceWorkerRegistrationPromise = null
  const currentControllerIsMarketing =
    'serviceWorker' in navigator &&
    isMarketingWorker(navigator.serviceWorker.controller)
  let registrationRemovalCompleted = false

  if ('serviceWorker' in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations()
      const marketingRegistrations = registrations.filter(
        isMarketingRegistration
      )
      const removalResults = await Promise.all(
        marketingRegistrations.map(registration => registration.unregister())
      )
      registrationRemovalCompleted =
        marketingRegistrations.length === 0 || removalResults.every(Boolean)
    } catch (error) {
      logger.warn('Could not unregister the offline experience:', error.message)
    }
  }

  await clearMarketingCaches()

  if (currentControllerIsMarketing && registrationRemovalCompleted) {
    window.location.reload()
  }
}

export function clearAnalyticsBrowserStorage() {
  try {
    window.sessionStorage.removeItem('blog_session_id')
    Object.keys(window.sessionStorage)
      .filter(key => /sentry|replay/i.test(key))
      .forEach(key => window.sessionStorage.removeItem(key))
  } catch (_error) {
    // Storage can be unavailable in restricted browser contexts.
  }
}
